import {
  Component,
  computed,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';

import {
  AwTableComponent,
  AwDividerComponent,
  AwActionBarComponent,
  ActionBarRight,
  AwSpinnerComponent,
  AwDialogComponent,
  AwToastComponent,
  ToastVariants,
  DialogOptions,
  TableRow,
} from '@assetworks-llc/aw-component-lib';
import {
  TableCellInput,
  TableCellTypes,
} from '@assetworks-llc/aw-component-lib';
import { AwDateFormComponent } from '../../../../shared/components/aw-date-form/aw-date-form.component';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { IntegrationService } from '../../../../core/services/integration.service';
import { ThemeService } from '../../../../core/services/theme.service';
import { AuthenticationService } from '../../../../core/services/authentication.service';
import { SecretService } from '../../../../core/services/secret.service';
import { EmployeeAuthenticationService } from '../../../../core/services/employee-authentication.service';
import { EmployeeService } from '../../../../core/services/employee.service';
import { UserService } from '../../../../core/services/user.service';
import { ParentIntegrationService } from '../../../../core/services/parent-integration.service';
import { Integration } from '../../../../api/models';
@Component({
  selector: 'app-integration-management',
  imports: [
    AwSpinnerComponent,
    AwToastComponent,
    AwDialogComponent,
    AwTableComponent,
    AwDividerComponent,
    CommonModule,
    AwTableComponent,
    AwDividerComponent,
    AwDateFormComponent,
    AwActionBarComponent,
  ],
  templateUrl: './integration-management.component.html',
  styleUrls: [
    './integration-management.component.scss',
    '../../../../../styles/management.scss',
  ],
  standalone: true,
})
export class IntegrationManagementComponent implements OnInit {
  constructor(
    private router: Router,
    private integrationService: IntegrationService,
    private authenticationService: AuthenticationService,
    private employeeAuthenticationService: EmployeeAuthenticationService,
    private secretService: SecretService,
    private themeService: ThemeService,
    private employeeService: EmployeeService,
    private userService: UserService,
    private parentIntegrationService: ParentIntegrationService
  ) {}

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  @ViewChild(AwTableComponent, { static: true })
  awTableComponent!: AwTableComponent;

  isLoading: boolean = false;

  container_theme = computed(() => this.themeService.themeSignal());
  checkedRows: TableRow[] = [];

  tableData = signal<any[]>([]);
  tableDataCopy: any[] = [];
  selectedDate: Date | null = null;
  enableStickyOptions = signal(true);
  sortDisabled = signal(false);

  integrationToDelete: TableRow | null = null;

  openDeleteIntegrationDialog: boolean = false;

  actionsRight: ActionBarRight[] = [];

  dialogOptionsDelete: DialogOptions = {
    variant: 'alert',
    title: `Delete Integration`,
    description: `By selecting Delete, you will permanently delete this information. Choose Cancel, to go back to the view.`,
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Delete',
  };

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  private fetchIntegrationsFromIds(integrationIds: string[]): Observable<Integration[]> {
    if (!integrationIds.length) return of([]);

    return forkJoin(
      integrationIds.map(id =>
        this.integrationService.getIntegrationById({ organizationId: id })
          .pipe(catchError(() => of(null)))
      )
    ).pipe(
      map(integrations => integrations.filter((i): i is Integration => i !== null))
    );
  }

  ngOnInit(): void {
    this.isLoading = true;

    const organization = this.userService.getOrganization();
    const organizationId = this.userService.getUserInfo()?.organizationId || '';
    const userPermissions = this.userService.getUserPermissions();

    let integrations$: Observable<Integration[] | undefined>;

    if (organization === 'assetworks-employee') {
      integrations$ = this.integrationService.getAlIntegrations();

      this.actionsRight = [{
        buttonCallback: {
          label: 'Add Integration Partner',
          buttonType: 'outlined',
          action: () => this.router.navigate(['/integration-management/add-integration']),
        },
      }];

      if (userPermissions['canDelete']) {
        this.columnsDefinition.push({
          type: TableCellTypes.IconButton,
          key: 'random',
          label: 'Remove',
          buttonType: 'primary',
          isExpanded: false,
          icon: 'delete',
          action: (data: TableRow) => {
            this.openDeleteIntegrationDialog = true;
            this.integrationToDelete = data;
          },
        });
      }
    } else if (organization === 'integration-employee') {
      integrations$ = this.parentIntegrationService
        .getParentIntegrationById({ organizationId })
        .pipe(
          switchMap(parentIntegration =>
            this.fetchIntegrationsFromIds(parentIntegration?.integrationIds || [])
          ),
          catchError(() => of([]))
        );

      this.actionsRight = [];
    } else {
      integrations$ = of([]);
      this.actionsRight = [];
    }

    integrations$
      .pipe(
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          this.buildToastMessage('Failed To Load Integrations', 'error');
          return of([]);
        })
      )
      .subscribe((integrations) => {
        const integrationData: any[] =
          integrations?.map((integration) => ({
            integrationInfo: {
              name: integration.organizationName,
              id: integration.organizationId,
              customers: integration.customerIds,
              link: 'https://www.google.com',
              detailsPageRoute: `/integration-management/integration-details/${integration.organizationId}`,
            },
            num_customers: integration.customerIds?.length ?? 0,
            modified_date: integration.modifiedOn
              ? new Date(integration.modifiedOn).toLocaleDateString()
              : 'N/A',
            date_created: integration.createdOn
              ? new Date(integration.createdOn).toLocaleDateString()
              : 'N/A',
          })) ?? [];
        this.tableData.set(integrationData);
        this.tableDataCopy = integrationData;
      });
  }

  columnsDefinition: TableCellInput[] = [
    {
      align: 'left',
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'integration_name',
      combineFields: [
        'integrationInfo.name',
        'integrationInfo.detailsPageRoute',
        'integrationInfo.id',
      ],
      label: 'Integration Name',
      combineTemplate: (values: any[]) => ({
        template: this.CustomTemplate(values),
      }),
      sort: true,
    },
    {
      type: TableCellTypes.Title,
      key: 'num_customers',
      label: 'Number of Customers',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
    {
      type: TableCellTypes.Title,
      key: 'modified_date',
      label: 'Last Modified',
      isExpanded: false,
      sort: true,
      align: 'center',
    },
    {
      type: TableCellTypes.Title,
      key: 'date_created',
      label: 'Created On',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
  ];
  CustomTemplate = (values: any) => `
  <div class="custom-template">
    <a href="${values[1]}">${values[0]}</a>
  <p>${values[2]}</p>
  </div>`;

  onDateSelected(date: Date | null) {
    if (date === null) {
      this.awTableComponent.applyFilter('');
      return;
    }
    const filteredDate: string = date!.toLocaleDateString();
    this.awTableComponent.applyFilter(filteredDate);
  }
  dialogFormDeleteHandler(formValues: any) {
    if (this.integrationToDelete) {
      const integrationId: string =
        this.integrationToDelete['integrationInfo'].id;

      this.isLoading = true;

      this.secretService
        .listAllSecrets({ organizationId: integrationId })
        .pipe(
          mergeMap((secrets) => {
            if (!secrets || secrets.length === 0) {
              return of([]);
            }
            return forkJoin(
              secrets.map((secret) =>
                this.secretService
                  .deleteSecret({
                    organizationId: integrationId,
                    secretName: secret.name ?? '',
                  })
                  .pipe(catchError(() => of(false)))
              )
            );
          }),
          catchError(() => of([])),
          mergeMap(() =>
            this.employeeService
              .getEmployeesByOrganization({ organizationId: integrationId })
              .pipe(
                mergeMap((employees) => {
                  if (!employees || employees.length === 0) {
                    return of([]);
                  }
                  return forkJoin(
                    employees.map((employee) =>
                      this.employeeService
                        .deleteEmployee({ organizationId: integrationId, employeeId: employee.id! })
                        .pipe(catchError(() => of(false)))
                    )
                  );
                }),
                catchError(() => of([]))
              )
          ),
          mergeMap(() =>
            forkJoin([
              this.authenticationService
                .getAuthenticationConfig({ organizationId: integrationId })
                .pipe(
                  mergeMap(() =>
                    this.authenticationService.deleteAuthenticationConfig({ organizationId: integrationId })
                  ),
                  catchError(() => of(false))
                ),
              this.employeeAuthenticationService
                .getAuthenticationConfig({ organizationId: integrationId })
                .pipe(
                  mergeMap(() =>
                    this.employeeAuthenticationService.deleteAuthenticationConfig({ organizationId: integrationId })
                  ),
                  catchError(() => of(false))
                ),
            ])
          ),
          mergeMap(() =>
            this.integrationService.deleteIntegration({
              organizationId: integrationId,
            })
          ),
          finalize(() => {
            this.isLoading = false;
            this.openDeleteIntegrationDialog = false;
            this.integrationToDelete = null;
          })
        )
        .subscribe({
          next: (deleted) => {
            const filtered = this.tableDataCopy.filter(
              (row) => row['integrationInfo'].id !== integrationId
            );
            this.tableData.set(filtered);
            this.tableDataCopy = [...filtered];
            this.buildToastMessage(
              'Successfully deleted integration',
              'success'
            );
          },
          error: () => {
            this.buildToastMessage('Failed to delete integration', 'error');
          }
        });
    }
  }
  dialogCloseHandler() {
    this.openDeleteIntegrationDialog = false;
  }
}
